import React, {
  ComponentProps,
  FunctionComponent,
  useEffect,
  useState,
} from "react";
import { TRootState } from "../../store/create-store";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Functions.module.css";
import { round } from "mathjs";
import ColumnButton from "../../components/ColumnButton";
import { rootActions } from "../../reducers/root.slice";
import Table, { TableCell } from "../../components/Table";
import SectionTitle from "../../components/SectionTitle";
import withErrorBoundary from "../../hocs/withErrorBoundary";
import Icon from "../../components/Icon";
import Typography from "../../components/Typography";
import Categories from "./Categories";
import LabelTitle from "../../components/LabelTitle";
import Switch from "../../components/Switch";
import {
  selectLeftPanelState,
  selectRightPanelState,
  selectRootState,
} from "../../selectors";
import classNames from "classnames";
import Spinner from "../../components/Spinner";
import { ScoreHeaderCell, ScoreValueCell } from "../Scores/ScoreRow";
import { selectPCAForSelectedMethod } from "../../selectors/index.selector";
import ColumnNameUtils from "../../utils/ColumnNameUtils";

type TProps = {
  label: string;
  value: boolean;
  disabled?: boolean;
  subField?: boolean;
  loading?: boolean;
  onChange: () => void;
  icon?: ComponentProps<typeof Icon>["name"];
};

const PCAScores = () => {
  const pca = useSelector(selectPCAForSelectedMethod);

  return (
    <table>
      <thead>
        <tr>
          <ScoreHeaderCell classname={styles.pcaScoreCell} label="PCA_x" />
          <ScoreHeaderCell classname={styles.pcaScoreCell} label="PCA_y" />
          <ScoreHeaderCell classname={styles.pcaScoreCell} label="PCA_z" />
        </tr>
      </thead>
      <tbody>
        <tr>
          <ScoreValueCell classname={styles.pcaScoreCell} value={pca?.x} />
          <ScoreValueCell classname={styles.pcaScoreCell} value={pca?.y} />
          <ScoreValueCell classname={styles.pcaScoreCell} value={pca?.z} />
        </tr>
      </tbody>
    </table>
  );
};

const SwitchField: FunctionComponent<TProps> = (props) => (
  <li className={styles.listItem}>
    <label
      className={classNames(styles.field, {
        [styles.subField]: props.subField,
      })}
    >
      {props.icon && <Icon name="blur_on" />}
      <Typography variant="body">{props.label}</Typography>
      {props.loading && <Spinner />}
    </label>

    <Switch
      disabled={props.disabled}
      value={props.value}
      onChange={props.onChange}
    />
  </li>
);

const Functions: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [showAllStats, setShowAllStats] = useState<boolean>(false);
  const {
    computedValues,
    sdFilter,
    sdMax,
    sdMin,
    isContinuous,
    selectedColumn,
    applyPCA,
    showDensityPlot,
    hasPCA,
    hasPCAProjection,
    projectToPCA,
    projectToPCAInProgress,
    applyZScore,
    applyMinMaxScaling,
    showPCAAxis,
  } = useSelector(selectFunctions);

  useEffect(() => {
    dispatch(rootActions.generateGraph());
  }, [
    sdFilter,
    applyPCA,
    hasPCA,
    projectToPCA,
    applyZScore,
    applyMinMaxScaling,
    showPCAAxis,
  ]);

  const title = selectedColumn ? (
    <LabelTitle>{ColumnNameUtils.removePrefixes(selectedColumn)}</LabelTitle>
  ) : null;

  const setRightPanel = (key: string, value: boolean) => {
    dispatch(
      rootActions.setRightPanel({
        [key]: value,
      })
    );
  };

  const common = (
    <>
      {title}
      <Categories />
      <SectionTitle>Transforms</SectionTitle>

      <ul>
        <SwitchField
          label="Min-Max Scaling"
          value={applyMinMaxScaling}
          onChange={() =>
            setRightPanel("applyMinMaxScaling", !applyMinMaxScaling)
          }
        />

        <SwitchField
          label="Standardization (z-score)"
          value={applyZScore}
          onChange={() => setRightPanel("applyZScore", !applyZScore)}
        />

        <li className={styles.gap} />

        <li className={styles.listItem}>
          <label className={styles.field}>
            <Typography variant="body">PCA</Typography>
            {!hasPCA && <Spinner />}
          </label>
        </li>

        {hasPCA && (
          <li className={classNames(styles.listItem, styles.pcaScores)}>
            <PCAScores />
          </li>
        )}

        <SwitchField
          subField
          label="Align with axis of maximum variance"
          value={applyPCA}
          disabled={true}
          onChange={() => setRightPanel("applyPCA", !applyPCA)}
        />

        <SwitchField
          subField
          label="Show PCA axis"
          value={showPCAAxis}
          disabled={!hasPCA}
          onChange={() => setRightPanel("showPCAAxis", !showPCAAxis)}
        />

        <SwitchField
          subField
          label="Project to PCA"
          loading={projectToPCAInProgress}
          value={projectToPCA || projectToPCAInProgress}
          disabled={!hasPCA || projectToPCAInProgress}
          onChange={() => {
            if (projectToPCA) {
              setRightPanel("projectToPCA", false);
            } else if (hasPCAProjection) {
              setRightPanel("projectToPCA", true);
            } else {
              dispatch(rootActions.projectToPCA());
            }
          }}
        />

        <SwitchField
          subField
          label="Scale by eigen value"
          value={false}
          disabled={true}
          onChange={() => setRightPanel("showDensityPlot", !showDensityPlot)}
        />

        <li className={styles.gap} />

        <li className={styles.listItem}>
          <label className={styles.field}>
            <Typography variant="body">Representations</Typography>
          </label>
        </li>

        <SwitchField
          label="Density Plot"
          value={showDensityPlot}
          icon="blur_on"
          onChange={() => setRightPanel("showDensityPlot", !showDensityPlot)}
        />
      </ul>
    </>
  );

  const allStats = (
    <>
      <SectionTitle onClick={() => setShowAllStats(!showAllStats)}>
        <Icon
          name={showAllStats ? "expand_less" : "expand_more"}
          color="dark"
        />
        All Stats
      </SectionTitle>

      {showAllStats && (
        <Table>
          <thead>
            <tr>
              <TableCell type="header">Column</TableCell>
              <TableCell type="header">Mean</TableCell>
              <TableCell type="header">SD</TableCell>
            </tr>
          </thead>
          <tbody>
            {Object.entries(computedValues.standardDeviations).map(
              ([column, sd]) => (
                <tr key={column}>
                  <TableCell type="key">{column}</TableCell>

                  <TableCell type="value">
                    {round(computedValues.means[column], 4)}
                  </TableCell>

                  <TableCell type="value">{round(sd, 4)}</TableCell>
                </tr>
              )
            )}
          </tbody>
        </Table>
      )}
    </>
  );

  if (!isContinuous) {
    return (
      <div className={styles.wrapper}>
        {common}

        {allStats}
      </div>
    );
  }

  const currentMean = selectedColumn
    ? round(computedValues.means[selectedColumn], 4)
    : "";
  const currentSD = selectedColumn
    ? round(computedValues.standardDeviations[selectedColumn], 4)
    : "";
  const currentMin = selectedColumn
    ? round(computedValues.smallestValue[selectedColumn], 4)
    : "";
  const currentMax = selectedColumn
    ? round(computedValues.largestValue[selectedColumn], 4)
    : "";

  const onSDFilter = (sdFilter: number) => {
    dispatch(
      rootActions.setRightPanel({
        sdFilter,
      })
    );
  };

  return (
    <div className={styles.wrapper}>
      {common}

      <SectionTitle>Distribution</SectionTitle>

      <div className={styles.sdWrapper}>
        <ColumnButton isActive={sdFilter === 1} onClick={() => onSDFilter(1)}>
          1σ
        </ColumnButton>
        <ColumnButton isActive={sdFilter === 2} onClick={() => onSDFilter(2)}>
          2σ
        </ColumnButton>
        <ColumnButton isActive={sdFilter === 3} onClick={() => onSDFilter(3)}>
          3σ
        </ColumnButton>
        <input
          className={styles.input}
          type="number"
          step="0.1"
          disabled={!isContinuous}
          value={sdFilter}
          onChange={(event) => onSDFilter(Number(event.target.value))}
        />
      </div>

      <Typography variant="body">
        Showing values in{" "}
        <Typography variant="code">
          [{round(sdMin, 4)}, {round(sdMax, 4)}]
        </Typography>
      </Typography>

      <SectionTitle>STATS</SectionTitle>

      <Table>
        <tbody>
          <tr>
            <TableCell type="key">Mean</TableCell>
            <TableCell type="value">{currentMean}</TableCell>
          </tr>
          <tr>
            <TableCell type="key">SD</TableCell>
            <TableCell type="value">{currentSD}</TableCell>
          </tr>
          <tr>
            <TableCell type="key">Smallest value</TableCell>
            <TableCell type="value">{currentMin}</TableCell>
          </tr>
          <tr>
            <TableCell type="key">Largest value</TableCell>
            <TableCell type="value">{currentMax}</TableCell>
          </tr>
        </tbody>
      </Table>

      {allStats}
    </div>
  );
};

export const selectFunctions = (state: TRootState) => {
  const { computedValues, continuousColumns } = selectRootState(state);

  const leftPanel = selectLeftPanelState(state);
  const rightPanel = selectRightPanelState(state);

  const pcaForMethod = selectPCAForSelectedMethod(state);
  const hasPCA = Boolean(pcaForMethod);
  const hasPCAProjection = Boolean(pcaForMethod?.adjustedData);

  return {
    computedValues,
    sdFilter: rightPanel.sdFilter,
    sdMin: rightPanel.sdMin,
    sdMax: rightPanel.sdMax,
    isContinuous:
      !!leftPanel.selectedColumn &&
      continuousColumns.includes(leftPanel.selectedColumn),
    selectedColumn: leftPanel.selectedColumn,
    applyPCA: rightPanel.applyPCA,
    showDensityPlot: rightPanel.showDensityPlot,
    projectToPCA: rightPanel.projectToPCA,
    projectToPCAInProgress: rightPanel.projectToPCAInProgress,
    hasPCA,
    hasPCAProjection,
    applyZScore: rightPanel.applyZScore,
    applyMinMaxScaling: rightPanel.applyMinMaxScaling,
    showPCAAxis: rightPanel.showPCAAxis,
  };
};

export default withErrorBoundary(Functions);
